import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BarChart3 } from 'lucide-react';
import DonutChart from '../components/DonutChart';
import { ApiService } from '../utils/api';
import { formatDateTime, formatTime } from '../utils/datetime';
import { STATUS_LABELS } from '../utils/registrationFilterParams';

const REFRESH_MS = 30000;
const SOURCE_LABELS = { ONLINE: 'Online', 'ON-SPOT': 'On-spot', ON_SPOT: 'On-spot', BULK_UPLOAD: 'Bulk upload' };

const toSlices = (counts = [], labels = {}) => counts.map((s) => ({
  key: s.value ?? '__none',
  label: s.value == null ? 'Not answered' : labels[s.value] || s.value,
  count: s.count,
}));

/** Read-only live stats for organisers, opened from a time-limited link; works on a big screen or a phone. */
const SharedStatsPage = () => {
  const { token } = useParams();
  const { data, error, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ['sharedStats', token],
    queryFn: () => ApiService.getSharedStats(token),
    refetchInterval: REFRESH_MS,
    retry: false,
  });

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center text-gray-500">Loading live stats…</div>;
  }
  if (error || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="max-w-md rounded-xl border bg-white p-8 text-center shadow-sm">
          <BarChart3 className="mx-auto mb-3 h-10 w-10 text-gray-400" />
          <h1 className="text-lg font-semibold text-gray-900">Link not available</h1>
          <p className="mt-1 text-sm text-gray-600">{error?.message || 'This stats link has expired or was revoked.'} Ask the event team for a new one.</p>
        </div>
      </div>
    );
  }

  const analytics = data.analytics || {};
  const scans = (analytics.scanCounts || []).filter((s) => !s.deleted || s.totalScans > 0);
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
        <header className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Live event stats</p>
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{data.eventName}</h1>
          </div>
          <p className="text-xs text-gray-500">
            Updated {formatTime(new Date(dataUpdatedAt))} · refreshes every 30s · link valid until {formatDateTime(data.expiresAt)}
          </p>
        </header>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          <div className="rounded-xl border bg-white p-5">
            <p className="text-sm text-gray-500">Registrations</p>
            <p className="text-4xl font-bold tabular-nums text-gray-900">{(analytics.totalRegistrations || 0).toLocaleString('en-IN')}</p>
          </div>
          {scans.map((scan) => (
            <div key={scan.purpose} className="rounded-xl border bg-white p-5">
              <p className="truncate text-sm text-gray-500">{scan.name}</p>
              <p className="text-4xl font-bold tabular-nums text-blue-700">{(scan.totalScans || 0).toLocaleString('en-IN')}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <DonutChart title="Registrations by status" slices={toSlices(analytics.statusCounts, STATUS_LABELS)} />
          <DonutChart title="Registrations by source" slices={toSlices(analytics.sourceCounts, SOURCE_LABELS)} />
          {(analytics.fields || []).map((field) => (
            <DonutChart
              key={`${field.parentLabel || ''}/${field.label}`}
              title={field.parentLabel ? `${field.label} (${field.parentLabel})` : field.label}
              slices={toSlices(field.counts)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SharedStatsPage;
